import React from 'react';
import styled from 'styled-components';
import BanchiAddressLineFieldContainer from '../../../app/common/fields/BanchiAddressLineField/BanchiAddressLineFieldContainer';
import CityField from '../../../app/common/fields/CityField';
import CompanyNameField from '../../../app/common/fields/CompanyNameField';
import MobileEmailField from '../../../app/common/fields/MobileEmail/MobileEmailField';
import JPPhoneNumberField from '../../../app/common/fields/phoneNumber/JPPhoneNumber';
import JPPostalCodeFieldContainer from '../../../app/common/fields/postalcode/JPPostalCodeFieldContainer';
import StateField from '../../../app/common/fields/state/StateField';
import { IAddress, IAddressFieldConfig } from '../address/address.interface';
import JobTitleField from '../../../app/common/fields/JobTitleField';
import {
    TwoColFormWrapper,
    TwoColFormLeftElement,
    TwoColFormRightElement,
    SingleFormElementWrapper,
} from '../../../app/common/components/FormLayoutStyles';
import { MQS, COLORS } from '../../../app/common/constants/styles';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { DEFAULT_JP_ADDRESS_FIELD_CONFIG } from '../addressFormConfig';

const FieldsWrapper = styled.div``;

const AddressInfo = styled.div`
    color: ${COLORS.grey7};
    line-height: 1.5;
    flex: 2 3 0px;
    font-size: 0.875rem;

    a {
        color: ${COLORS.rakutenRed};
        font-weight: 500;

        &,
        &:active,
        &:hover,
        &:focus {
            text-decoration: none;
        }
    }

    @media (max-width: ${MQS.mq2Min}) {
        margin-top: 10px;
        padding: 0;
    }
`;

interface IAddressFormFieldProps {
    autoFocus?: boolean;
    values: Partial<IAddress>;
    isEditing?: boolean;
    stateInputDisabled: boolean;
    addressFieldConfig?: IAddressFieldConfig[][];
}
export class AddressFormFieldsJP extends React.Component<IAddressFormFieldProps> {
    private renderField(field: IAddressFieldConfig) {
        const { values, stateInputDisabled } = this.props;
        switch (field.name) {
            case 'postalCode':
                return (
                    <JPPostalCodeFieldContainer
                        key={field.name}
                        value={values.postalCode}
                        required={field.required ?? true}
                    />
                );
            case 'state':
                return (
                    <StateField
                        key={field.name}
                        countryCode={values.countryCode}
                        stateCodeFieldId="stateCode"
                        stateCodeFieldValue={values.stateCode}
                        stateNameFieldId="state"
                        stateNameFieldValue={values.state}
                        value={values.state}
                        required={field.required ?? true}
                        stateInputDisabled={stateInputDisabled}
                    />
                );
            case 'city':
                return (
                    <CityField
                        key={field.name}
                        className="notranslate"
                        value={values.city || ''}
                        required={field.required ?? true}
                        postalCode={values.postalCode}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'banchiAddress':
                return (
                    <BanchiAddressLineFieldContainer
                        key={field.name}
                        value={values.addressLine1}
                        required={field.required ?? true}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'companyName':
                return (
                    <CompanyNameField
                        key={field.name}
                        value={values.companyName || ''}
                        required={field.required ?? false}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'jobTitle':
                return (
                    <JobTitleField
                        key={field.name}
                        value={values.positionName || ''}
                        required={field.required ?? false}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'phoneNumber':
                return (
                    <JPPhoneNumberField
                        key={field.name}
                        value={values.phoneNumber || ''}
                        fieldId={'phoneNumber'}
                        label={'phone_number'}
                        required={field.required ?? true}
                        tooltipText="This phone number is used to solve delivery issues"
                        postalCode={values.postalCode}
                    />
                );
            case 'faxNumber':
                return (
                    <JPPhoneNumberField
                        key={field.name}
                        value={values.faxNumber || ''}
                        required={field.required ?? false}
                        fieldId={'faxNumber'}
                        label={'fax_number'}
                        postalCode={values.postalCode}
                    />
                );
            case 'mobilePhone':
                return (
                    <JPPhoneNumberField
                        key={field.name}
                        fieldId={'mobilePhoneNumber'}
                        label={'mobile_phone'}
                        value={values.mobilePhoneNumber || ''}
                        required={field.required ?? false}
                        postalCode={values.postalCode}
                    />
                );
            case 'mobileEmail':
                return (
                    <MobileEmailField
                        key={field.name}
                        value={values.mobileEmail || ''}
                        required={field.required ?? false}
                    />
                );
            default:
                return null;
        }
    }

    public render() {
        const { addressFieldConfig } = this.props;
        const renderConfig =
            addressFieldConfig && addressFieldConfig.length > 0
                ? addressFieldConfig
                : DEFAULT_JP_ADDRESS_FIELD_CONFIG;

        return (
            <FieldsWrapper>
                {renderConfig.map((itemConfigs, index) => {
                    if (itemConfigs.length === 2) {
                        return (
                            <TwoColFormWrapper key={index}>
                                <TwoColFormLeftElement>
                                    {this.renderField(itemConfigs[0])}
                                </TwoColFormLeftElement>
                                <TwoColFormRightElement>
                                    {this.renderField(itemConfigs[1])}
                                </TwoColFormRightElement>
                            </TwoColFormWrapper>
                        );
                    } else if (itemConfigs.length === 1) {
                        return (
                            <SingleFormElementWrapper key={index}>
                                {this.renderField(itemConfigs[0])}
                            </SingleFormElementWrapper>
                        );
                    } else {
                        return null;
                    }
                })}
                <AddressInfo>
                    {TranslationHelpers.getFormattedMessage('address.email.info')}
                </AddressInfo>
            </FieldsWrapper>
        );
    }
}
